import React, { useState, useEffect } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { Card } from '@components/common/UIComponents';
import { getUnlockedAchievements } from '@services/firestoreService';
import { ACHIEVEMENTS, ACHIEVEMENT_CATEGORIES, getAchievementsByCategory } from '@utils/achievements';
import { Trophy, Lock } from 'lucide-react';

const Achievements = () => {
  const { user } = useAuth();
  const [unlockedAchievements, setUnlockedAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    loadAchievements();
  }, [user]);

  const loadAchievements = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const unlocked = await getUnlockedAchievements(user.uid);
      setUnlockedAchievements(unlocked);
    } catch (error) {
      console.error('Error loading achievements:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAchievements = selectedCategory === 'all' 
    ? ACHIEVEMENTS 
    : getAchievementsByCategory(selectedCategory);

  const unlockedCount = unlockedAchievements.length;
  const totalCount = ACHIEVEMENTS.length;
  const progress = (unlockedCount / totalCount) * 100;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          <Trophy size={32} className="text-yellow-500" />
          Achievements
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Track your progress and unlock badges
        </p>
      </div>

      {/* Progress Overview */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {unlockedCount} / {totalCount}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">Achievements Unlocked</p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-primary-600">{progress.toFixed(0)}%</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Completion</p>
          </div>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
          <div
            className="bg-gradient-to-r from-primary-500 to-primary-600 h-3 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </Card>

      {/* Category Filter */}
      <Card>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              selectedCategory === 'all'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setSelectedCategory(ACHIEVEMENT_CATEGORIES.STREAK)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              selectedCategory === ACHIEVEMENT_CATEGORIES.STREAK
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            Streaks
          </button>
          <button
            onClick={() => setSelectedCategory(ACHIEVEMENT_CATEGORIES.MILESTONE)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              selectedCategory === ACHIEVEMENT_CATEGORIES.MILESTONE
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            Milestones
          </button>
          <button
            onClick={() => setSelectedCategory(ACHIEVEMENT_CATEGORIES.GOAL)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              selectedCategory === ACHIEVEMENT_CATEGORIES.GOAL
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            Goals
          </button>
          <button
            onClick={() => setSelectedCategory(ACHIEVEMENT_CATEGORIES.SPECIAL)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              selectedCategory === ACHIEVEMENT_CATEGORIES.SPECIAL
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            Special
          </button>
        </div>
      </Card>

      {/* Achievements Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredAchievements.map((achievement) => {
          const Icon = achievement.icon;
          const isUnlocked = unlockedAchievements.includes(achievement.id);

          return (
            <Card
              key={achievement.id}
              className={`${
                isUnlocked
                  ? 'border-2 border-yellow-400 dark:border-yellow-500'
                  : 'opacity-60 grayscale'
              } transition-all hover:scale-105`}
            >
              <div className="flex items-start gap-4">
                <div className={`${isUnlocked ? achievement.bg : 'bg-gray-200 dark:bg-gray-700'} p-3 rounded-lg relative`}>
                  <Icon size={32} className={isUnlocked ? achievement.color : 'text-gray-400'} />
                  {!isUnlocked && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Lock size={16} className="text-gray-600" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                    {achievement.name}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {achievement.description}
                  </p>
                  {isUnlocked && (
                    <span className="inline-block px-2 py-1 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 rounded">
                      Unlocked! 🎉
                    </span>
                  )}
                  {!isUnlocked && (
                    <span className="inline-block px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                      Locked 🔒
                    </span>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default Achievements;
